import React, { useRef, useState, useEffect, memo } from 'react';
import { css, useTheme } from '@emotion/react';
import { ProductCardSkeleton } from '../../Skeleton/ProductCard/ProductCard';
import { TrendingProductItems } from './index';

type TrendingProductItemsProps = {
  isMiniCart?: boolean;
  showImageZoom?: boolean;
  wineType?: string[] | null;
  grape?: string[] | null;
  country?: string[] | null;
  productType?: string[] | null;
  awardWinner?: boolean;
  giftItems?: boolean;
  offerItems?: boolean;
  fineWine?: boolean;
  widgetTitle?: string;
  maxRecommendations?: number;
};

type LazyTrendingProductsProps = TrendingProductItemsProps & {
  rootMargin?: string;
  skeletonCount?: number;
};

export const LazyTrendingProducts = memo(
  ({ rootMargin = '200px', skeletonCount = 5, ...trendingProps }: LazyTrendingProductsProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
      const element = containerRef.current;
      if (!element) {
        return;
      }

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.disconnect();
          }
        },
        { rootMargin },
      );

      observer.observe(element);

      return () => {
        observer.disconnect();
      };
    }, [rootMargin]);

    const theme = useTheme();
    const productItemTrending = css`
      ${theme.widgets.Cart.productItemsTrending}
    `;

    const skeletonContainerStyle = css`
      display: flex;
      gap: 16px;
      overflow: hidden;
    `;

    if (isVisible) {
      return <TrendingProductItems {...trendingProps} />;
    }

    return (
      <div
        ref={containerRef}
        className="trending-items-container"
        data-testid="lazy-trending-items"
        css={productItemTrending}
        style={{ position: 'relative' }}
      >
        <div css={skeletonContainerStyle}>
          {Array.from({ length: skeletonCount }, (_, i) => (
            <ProductCardSkeleton key={i} />
          ))}
        </div>
      </div>
    );
  },
);
